/**
 * Client API Service
 *
 * Feature: 136-dashboard-alignment (T009), 126-client-assignment-views (T007)
 * Description: Frontend API client for client entity endpoints.
 *   Used by ManagerDashboard for client breakdown widget and Client Detail page.
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards)
 */

import { apiClient } from './apiClient';
import type {
  ClientDetail,
  ClientDetailResponse,
  CapacityAssignmentView,
  CapacityAssignmentsResponse,
  WorkspaceAssignmentView,
  WorkspaceAssignmentsResponse,
} from '@shared/types/client-detail';

export interface ClientSummaryRow {
  id: string;
  client_account_id: string;
  client_name: string;
  sla_tier: string | null;
  contract_value: number | null;
  is_active: boolean;
  capacity_count: number;
  workspace_count: number;
}

export interface ClientSummary {
  totalClients: number;
  activeClients: number;
  inactiveClients: number;
  bySlaTier: Record<string, number>;
  clients: ClientSummaryRow[];
}

// Spec 178 US1 — live, searchable client list (camelCase, US4)
export interface ClientListItem {
  id: string;
  name: string;
  code: string;
  slaTier: string | null;
  isActive: boolean;
  capacityCount: number;
  workspaceCount: number;
  lastActivity: string;
}
export interface ClientListParams {
  search?: string;
  slaTier?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}
export interface ClientListResult {
  items: ClientListItem[];
  page: number;
  pageSize: number;
  total: number;
}

// Spec 178 US7 — blast radius
export interface BlastRadiusWorkspace {
  workspaceId: string;
  workspaceName: string;
  capacityName: string | null;
  lakehouseCount: number;
  warehouseCount: number;
  servesAllClients: boolean;
  linkType: 'direct' | 'via-capacity' | 'central-fabric';
}
export interface ClientBlastRadius {
  workspaces: BlastRadiusWorkspace[];
  totals: { workspaces: number; lakehouses: number; warehouses: number };
}
export interface WorkspaceClientImpact {
  workspaceId: string;
  workspaceName: string;
  servesAllClients: boolean;
  clients: Array<{ id: string; name: string; code: string; slaTier: string | null }>;
}

// Re-export types for convenience
export type { ClientDetail, CapacityAssignmentView, WorkspaceAssignmentView };

const API_BASE = '/api/clients';
const ASSIGNMENT_API_BASE = '/api/v1';

class ClientApiService {
  /**
   * All requests go through the authenticated axios `apiClient` so the MSAL
   * bearer token is attached. The assignment endpoints (`/api/v1/...`) sit
   * behind `authMiddleware`, so a raw `fetch` (no token) 401s. Using apiClient
   * keeps the already-public `/api/clients/*` calls working too.
   */
  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const method = (options.method ?? 'GET').toString();
    const data = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
    const response = await apiClient.request<T>({ url, method, data });
    return response.data;
  }

  /**
   * Get client summary data for Manager Dashboard.
   */
  async getSummary(): Promise<ClientSummary> {
    return this.request<ClientSummary>(`${API_BASE}/summary`);
  }

  /**
   * Get the paginated, searchable client list (Spec 178 US1).
   */
  async listClients(params: ClientListParams = {}): Promise<ClientListResult> {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.slaTier) q.set('slaTier', params.slaTier);
    if (params.isActive !== undefined) q.set('isActive', String(params.isActive));
    q.set('page', String(params.page ?? 1));
    q.set('pageSize', String(params.pageSize ?? 10));
    const response = await this.request<{ success: boolean; data: ClientListResult }>(
      `${API_BASE}?${q.toString()}`
    );
    return response.data;
  }

  /**
   * Client footprint — workspaces + assets impacted if this client is at risk (Spec 178 US7).
   */
  async getBlastRadius(clientId: string): Promise<ClientBlastRadius> {
    const r = await this.request<{ success: boolean; data: ClientBlastRadius }>(
      `${API_BASE}/${clientId}/blast-radius`
    );
    return r.data;
  }

  /**
   * Reverse blast radius — clients impacted by a workspace (Spec 178 US7).
   */
  async getWorkspaceImpact(workspaceId: string): Promise<WorkspaceClientImpact> {
    const r = await this.request<{ success: boolean; data: WorkspaceClientImpact }>(
      `${API_BASE}/by-workspace/${workspaceId}`
    );
    return r.data;
  }

  /**
   * Toggle the central-fabric flag on a workspace (Spec 178 US7, Admin-only).
   */
  async setServesAllClients(workspaceId: string, servesAllClients: boolean): Promise<void> {
    await this.request(`${API_BASE}/by-workspace/${workspaceId}/serves-all-clients`, {
      method: 'PUT',
      body: JSON.stringify({ servesAllClients }),
    });
  }

  /**
   * End a client-workspace assignment (Spec 178 US3 — soft end). Admin-only.
   */
  async endWorkspaceAssignment(clientId: string, assignmentId: string): Promise<void> {
    await this.request(`${ASSIGNMENT_API_BASE}/${clientId}/workspaces/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get single client detail for Client Detail page header (Feature 126 — T007)
   */
  async getClientById(clientId: string): Promise<ClientDetail> {
    const response = await this.request<ClientDetailResponse>(`${API_BASE}/${clientId}`);
    return response.data;
  }

  /**
   * Get capacity assignments for a client (Feature 126 — T007)
   * @param clientId - Client UUID
   * @param includeInactive - Include ended assignments (for "Show history" toggle)
   */
  async getCapacityAssignments(
    clientId: string,
    includeInactive = false
  ): Promise<CapacityAssignmentView[]> {
    const params = new URLSearchParams();
    if (includeInactive) {
      params.set('includeInactive', 'true');
    }
    const queryString = params.toString();
    const url = `${ASSIGNMENT_API_BASE}/${clientId}/capacities${queryString ? `?${queryString}` : ''}`;
    const response = await this.request<CapacityAssignmentsResponse>(url);
    return response.data;
  }

  /**
   * Get workspace assignments for a client (Feature 126 — T007)
   * @param clientId - Client UUID
   * @param includeInactive - Include ended assignments (for "Show history" toggle)
   */
  async getWorkspaceAssignments(
    clientId: string,
    includeInactive = false
  ): Promise<WorkspaceAssignmentView[]> {
    const params = new URLSearchParams();
    if (includeInactive) {
      params.set('includeInactive', 'true');
    }
    const queryString = params.toString();
    const url = `${ASSIGNMENT_API_BASE}/${clientId}/workspaces${queryString ? `?${queryString}` : ''}`;
    const response = await this.request<WorkspaceAssignmentsResponse>(url);
    return response.data;
  }
}

export const clientApi = new ClientApiService();
export default clientApi;
